import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from './api';
import { errorMessage } from './errors';
import { formatDateTime } from './kit/time';
import PickSelect from './shared/PickSelect';
import LongTextCell from '@/components/LongTextCell';
import { TableScroller } from '@/components/ui/table-scroller';

const PAGE = 50;

const sourceOf = (c) => {
  if (c.utm?.utm_source) return c.utm.utm_source;
  if (c.referrer) {
    try { return new URL(c.referrer).hostname; } catch { return c.referrer; }
  }
  return 'direct';
};

const who = (c) => {
  const p = c.payload || {};
  return [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email || 'Unknown';
};

// Newest-first list of what the public lead forms received (ticket 203). Admin-only on the
// server; a 403 or a failed load shows the reason instead of an empty list.
export default function LeadCapturesList({ forms }) {
  const [formId, setFormId] = useState('');
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setItems(null); setError(null);
    try {
      const params = { limit: PAGE, ...(formId ? { form_id: formId } : {}) };
      setItems((await api.get('/v2/leadgen/captures', { params })).data.items || []);
    } catch (e) { setError(errorMessage(e)); }
  }, [formId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="captures-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <strong className="text-sm" style={{ color: '#070B5F' }}>Recent captures</strong>
        <div className="w-48">
          <PickSelect
            label="Filter captures by form"
            labelClassName="sr-only"
            value={formId}
            onChange={setFormId}
            options={[{ value: '', label: 'All forms' }, ...forms.map((f) => ({ value: f.form_id, label: f.name }))]}
            testId="captures-form-filter"
          />
        </div>
      </div>
      {error ? (
        <p className="text-xs text-red-600 mt-2" data-testid="captures-error">
          Could not load captures: {error}{' '}
          <button onClick={load} className="underline">Retry</button>
        </p>
      ) : items === null ? (
        <p className="text-xs text-slate-400 mt-2">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-slate-400 mt-2" data-testid="captures-empty">No captures yet.</p>
      ) : (
        <TableScroller className="mt-2">
          <table className="w-full text-xs" data-testid="captures-table">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <th className="py-2">When</th><th className="py-2 pl-3">Lead</th><th className="py-2 pl-3">Form</th>
                <th className="py-2 pl-3">Source</th><th className="py-2 pl-3">Assigned to</th>
              </tr>
            </thead>
            <tbody>
              {items.map(c => (
                <tr key={c.capture_id} className="border-b border-slate-50" data-testid={`capture-${c.capture_id}`}>
                  <td className="py-2 whitespace-nowrap">{formatDateTime(c.captured_at)}</td>
                  <td className="py-2 pl-3">
                    {c.contact_id
                      ? <Link to={`/portal/contacts/${c.contact_id}`} className="font-medium hover:underline" style={{ color: '#070B5F' }}>{who(c)}</Link>
                      : <span className="font-medium">{who(c)}</span>}
                    <div className="text-slate-400">{[c.payload?.email, c.payload?.phone].filter(Boolean).join(' · ')}</div>
                  </td>
                  <td className="py-2 pl-3">{c.form_name || c.slug}</td>
                  <td className="py-2 pl-3"><LongTextCell id={`cap-src-${c.capture_id}`} text={sourceOf(c)} lines={1} maxWidth={140} minWidth="6rem" /></td>
                  <td className="py-2 pl-3">{c.assigned_name || <span className="text-slate-400">Unassigned</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      )}
      {items && items.length === PAGE && (
        <p className="text-[11px] text-slate-400 mt-2">Showing the latest {PAGE}. Pick a form to narrow the list.</p>
      )}
    </div>
  );
}
